import { action, computed, flow, makeObservable, observable } from 'mobx';
import moment from 'moment';
import {
  AdvancedFilterListOption,
  AdvancedFilterProperties,
  availableOptions,
  availableProperties,
  BooleanProperties,
  FilterPropertiesTypes,
  infoByBooleanState,
  initBooleanValues,
  initRangeValues,
  initSelectedOptionsValues,
  ListProperties,
  NotBooleanProperties,
  RangeProperties,
} from './types';
import { PER_PAGE } from 'src/constants/main';
import { ORDER_STATUS } from 'src/constants/order';
import { localeStore } from 'src/mobx/localesStore';
import { userStore } from 'src/mobx/userStore';
import client from 'src/services/client';
import logger from 'src/services/logger';
import { DateRanges, dateTimeFormatForRequest, defaultDateFormat, presetDatesDataMap } from '~/constants/dateTime';
import { isTimeBetween } from '~/helpers';

type FilterProperty = {
  title: string;
  type: FilterPropertiesTypes;
  propertyKey: AdvancedFilterProperties;
  subText: string;
};

type FilterBooleanProperty = {
  title: string;
  info: { on: string; off: string };
  checked: boolean;
  key: BooleanProperties;
};

const defaultPresetDates = [
  DateRanges.Tomorrow,
  DateRanges.Today,
  DateRanges.Yesterday,
  DateRanges.ThisWeek,
  DateRanges.ThisMonth,
  DateRanges.Next30Days,
  DateRanges.Next90Days,
];

type FilterValues = {
  [AdvancedFilterProperties.OrderType]?: string[];
  [AdvancedFilterProperties.Branch]?: string[];
  [AdvancedFilterProperties.OrderingChannel]?: string[];
  [AdvancedFilterProperties.PaymentMethod]?: string[];
  [AdvancedFilterProperties.Status]?: string[];
  [AdvancedFilterProperties.ShowClosed]?: boolean;
  [AdvancedFilterProperties.SeparateAhead]?: boolean;
  [AdvancedFilterProperties.Date]?: { from: string; to: string; preset: number | null };
  [AdvancedFilterProperties.Time]?: { from: string; to: string; preset: number | null };
};

interface AdvancedFilterStoreProps {
  name: string;
  parentStore: ParentStore;
  properties: AdvancedFilterProperties[];
  presetDates?: DateRanges[];
  allowDatesInTheFuture?: boolean;
  defaultDatePreset?: DateRanges;
}

interface ParentStore {
  handleAdvancedFilter: () => void;
}

class AdvancedFilterStore {
  constructor({
    name,
    parentStore,
    properties,
    presetDates = defaultPresetDates,
    allowDatesInTheFuture = false,
    defaultDatePreset = DateRanges.ThisMonthUntilNow,
  }: AdvancedFilterStoreProps) {
    makeObservable(this);
    this.parentStore = parentStore;
    this.allowDatesInTheFuture = allowDatesInTheFuture;
    this._enabledProperties = properties;
    this._availablePresetDates = presetDates;
    this._name = name;
    this._defaultDatePreset = defaultDatePreset;
  }

  private _name = '';
  private _uniqueKey = '';
  public allowDatesInTheFuture = false;
  private _defaultDatePreset = DateRanges.ThisMonthUntilNow;

  private parentStore: ParentStore;
  @observable private _availableProperties = availableProperties;
  @observable private _availablePresetDates: DateRanges[];
  @observable private _availableOptions = availableOptions;

  @observable private _enabledProperties: AdvancedFilterProperties[] = [];

  @observable private _selectedOptionsValues = initSelectedOptionsValues;
  @observable private _rangeValues = initRangeValues;
  @observable private _booleanValues = initBooleanValues;

  @observable private _tempSelectedOptionsValues = initSelectedOptionsValues;
  @observable private _tempRangeValues = initRangeValues;
  @observable private _tempBooleanValues = initBooleanValues;

  private _getPersistName(name: string, key: string) {
    return `advanced_filter_${name}_${key}`;
  }

  private _backup(key: string, data: any[] | boolean) {
    try {
      localStorage.setItem(this._getPersistName(this._name + this._uniqueKey, key), JSON.stringify(data));
    } catch (error) {
      logger.log(error);
    }
  }

  private _restore(key: string): any[] | boolean | null {
    try {
      const data = localStorage.getItem(this._getPersistName(this._name + this._uniqueKey, key));

      if (key === AdvancedFilterProperties.Date && !data) {
        const preset = this._defaultDatePreset;
        const range = presetDatesDataMap[preset].getRange();

        return [
          ['from', range.from],
          ['to', range.to],
          ['preset', preset],
        ];
      }

      return data ? JSON.parse(data) : null;
    } catch (error: any) {
      return null;
    }
  }

  public hydrate(uniqueKey: string = '') {
    this._uniqueKey = uniqueKey;
    for (const key in this._selectedOptionsValues) {
      if (Object.prototype.hasOwnProperty.call(this._selectedOptionsValues, key)) {
        const data = this._restore(key);
        if (Array.isArray(data)) {
          this._selectedOptionsValues[key as ListProperties] = observable.map(data);
        }
      }
    }
    for (const key in this._rangeValues) {
      if (Object.prototype.hasOwnProperty.call(this._rangeValues, key)) {
        const data = this._restore(key);
        if (Array.isArray(data)) {
          this._rangeValues[key as RangeProperties] = observable.map(data);
        }
      }
    }
    for (const key in this._booleanValues) {
      if (Object.prototype.hasOwnProperty.call(this._booleanValues, key)) {
        const data = this._restore(key);
        if (typeof data === 'boolean') {
          this._booleanValues[key as BooleanProperties] = data;
        }
      }
    }
  }

  @action applyFilter = () => {
    for (const key in this._selectedOptionsValues) {
      if (Object.prototype.hasOwnProperty.call(this._selectedOptionsValues, key)) {
        this._selectedOptionsValues[key as ListProperties] = observable.map(this._tempSelectedOptionsValues[key as ListProperties]);
        this._backup(key, Array.from(this._selectedOptionsValues[key as ListProperties].entries()));
      }
    }
    for (const key in this._rangeValues) {
      if (Object.prototype.hasOwnProperty.call(this._rangeValues, key)) {
        this._rangeValues[key as RangeProperties] = observable.map(this._tempRangeValues[key as RangeProperties]);
        this._backup(key, Array.from(this._rangeValues[key as RangeProperties].entries()));
      }
    }
    for (const key in this._booleanValues) {
      if (Object.prototype.hasOwnProperty.call(this._booleanValues, key)) {
        this._booleanValues[key as BooleanProperties] = this._tempBooleanValues[key as BooleanProperties];
        this._backup(key, this._booleanValues[key as BooleanProperties]);
      }
    }

    this.parentStore.handleAdvancedFilter();
  };

  @action makeTempDataForUI = () => {
    for (const key in this._selectedOptionsValues) {
      if (Object.prototype.hasOwnProperty.call(this._selectedOptionsValues, key)) {
        this._tempSelectedOptionsValues[key as ListProperties] = observable.map(this._selectedOptionsValues[key as ListProperties]);
      }
    }
    for (const key in this._rangeValues) {
      if (Object.prototype.hasOwnProperty.call(this._rangeValues, key)) {
        this._tempRangeValues[key as RangeProperties] = observable.map(this._rangeValues[key as RangeProperties]);
      }
    }
    for (const key in this._booleanValues) {
      if (Object.prototype.hasOwnProperty.call(this._booleanValues, key)) {
        this._tempBooleanValues[key as BooleanProperties] = this._booleanValues[key as BooleanProperties];
      }
    }
  };

  @action resetPropertyValues = (propertyKey: NotBooleanProperties) => {
    if (propertyKey === AdvancedFilterProperties.Date || propertyKey === AdvancedFilterProperties.Time) {
      this._tempRangeValues[propertyKey] = observable.map(initRangeValues[propertyKey]);
    } else {
      this._tempSelectedOptionsValues[propertyKey] = observable.map(initSelectedOptionsValues[propertyKey]);
    }
  };

  @action selectAllOptions = (propertyKey: ListProperties) => {
    this._tempSelectedOptionsValues[propertyKey] = observable.map(this._availableOptions[propertyKey]);
  };

  @action resetAllValues = () => {
    for (const key in initSelectedOptionsValues) {
      if (Object.prototype.hasOwnProperty.call(initSelectedOptionsValues, key)) {
        this._tempSelectedOptionsValues[key as ListProperties] = observable.map(initSelectedOptionsValues[key as ListProperties]);
      }
    }
    for (const key in initRangeValues) {
      if (Object.prototype.hasOwnProperty.call(initRangeValues, key)) {
        this._tempRangeValues[key as RangeProperties] = observable.map(initRangeValues[key as RangeProperties]);
      }
    }
    for (const key in initBooleanValues) {
      if (Object.prototype.hasOwnProperty.call(initBooleanValues, key)) {
        this._tempBooleanValues[key as BooleanProperties] = initBooleanValues[key as BooleanProperties];
      }
    }
  };

  @computed get selectedOptions() {
    return this._tempSelectedOptionsValues;
  }

  @computed get selectedRanges() {
    return this._tempRangeValues;
  }

  @action toggleBooleanProperty = (propertyKey: BooleanProperties, value: boolean) => {
    this._tempBooleanValues[propertyKey] = value;
  };

  @action selectOption = (propertyKey: ListProperties, option: AdvancedFilterListOption) => {
    if (this._tempSelectedOptionsValues[propertyKey].has(option.value)) {
      this._tempSelectedOptionsValues[propertyKey].delete(option.value);
    } else {
      this._tempSelectedOptionsValues[propertyKey].set(option.value, option);
    }
  };

  @action selectRange = (propertyKey: RangeProperties, { from, to, preset = null }: { from: number; to: number; preset?: number | null }) => {
    this._tempRangeValues[propertyKey].set('from', from);
    this._tempRangeValues[propertyKey].set('to', to);
    this._tempRangeValues[propertyKey].set('preset', preset);
  };

  @action fetchBranchOptions = flow(function* (this: AdvancedFilterStore) {
    try {
      const { data = [] }: { data: { id: number; name: { en: string; ar: string } }[] } = yield client.get(`/v4/branches`, {
        params: {
          is_delete: 0,
          branches: userStore.user.branches.map((branch) => branch.id),
          per_page: PER_PAGE.MAXIMUM,
          // status: 1,
        },
      });

      const branches = data
        .map<[string, { value: string; label: string }]>(({ id, name }) => [
          String(id),
          { label: name[localeStore.language] || name.en, value: String(id) },
        ])
        .sort((a, b) => a[1].label.localeCompare(b[1].label));

      this._availableOptions[AdvancedFilterProperties.Branch] = observable.map(branches);
    } catch (error: any) {
      logger.log(error);
    }
  });

  @computed get properties() {
    return this._enabledProperties.reduce<FilterProperty[]>((properties, propertyKey: AdvancedFilterProperties) => {
      if (propertyKey === AdvancedFilterProperties.ShowClosed || propertyKey === AdvancedFilterProperties.SeparateAhead) {
        return properties;
      }

      const { title, type } = this._availableProperties[propertyKey];
      let subText = '';

      if (propertyKey === AdvancedFilterProperties.Date) {
        const from = this._tempRangeValues[propertyKey].get('from');
        const to = this._tempRangeValues[propertyKey].get('to');
        const preset = this._tempRangeValues[propertyKey].get('preset');

        if (typeof preset === 'number') {
          const range = presetDatesDataMap[preset as DateRanges].getRange();
          subText = `(${moment(range.from).format(defaultDateFormat)}-${moment(range.to).format(defaultDateFormat)})`;
          // subText = `${presetDatesDataMap[preset as DateRanges].title}`;
        } else if (from && to) {
          subText = `(${moment(from).format(defaultDateFormat)}-${moment(to).format(defaultDateFormat)})`;
        }
      } else if (propertyKey === AdvancedFilterProperties.Time) {
        const from = this._tempRangeValues[propertyKey].get('from');
        const to = this._tempRangeValues[propertyKey].get('to');
        subText = '';

        if (from && to) {
          subText = `(${moment(from).format('hh:mm A')}-${moment(to).format('hh:mm A')})`;
        }
      } else {
        subText =
          this._tempSelectedOptionsValues[propertyKey] && this._tempSelectedOptionsValues[propertyKey].size > 0
            ? `(${this._tempSelectedOptionsValues[propertyKey].size})`
            : '';
      }

      properties.push({
        title,
        propertyKey,
        type,
        subText,
      });

      return properties;
    }, []);
  }

  public orderInFilter(values: { [key in AdvancedFilterProperties]?: string }): boolean {
    return !this._enabledProperties.some((propertyKey: AdvancedFilterProperties) => {
      switch (propertyKey) {
        case AdvancedFilterProperties.Branch:
          return this._selectedOptionsValues[propertyKey].size > 0 && !this._selectedOptionsValues[propertyKey].has(values[propertyKey]);
        case AdvancedFilterProperties.OrderType:
          return this._selectedOptionsValues[propertyKey].size > 0 && !this._selectedOptionsValues[propertyKey].has(values[propertyKey]);
        case AdvancedFilterProperties.OrderingChannel:
          return this._selectedOptionsValues[propertyKey].size > 0 && !this._selectedOptionsValues[propertyKey].has(values[propertyKey]);
        case AdvancedFilterProperties.PaymentMethod:
          return this._selectedOptionsValues[propertyKey].size > 0 && !this._selectedOptionsValues[propertyKey].has(values[propertyKey]);
        case AdvancedFilterProperties.Status:
          return this._selectedOptionsValues[propertyKey].size > 0
            ? !this._selectedOptionsValues[propertyKey].has(values[propertyKey])
            : !this._availableOptions[propertyKey].has(values[propertyKey] as ORDER_STATUS);
        case AdvancedFilterProperties.Date: {
          const dateValues = this._getDateValues();
          return dateValues && !moment(values[propertyKey]).isBetween(dateValues.from, dateValues.to, undefined, '[]');
        }
        case AdvancedFilterProperties.Time: {
          const timeValues = this._getTimeValues();
          return (
            timeValues &&
            !isTimeBetween(timeValues.from.format('HH:mm:ss'), timeValues.to.format('HH:mm:ss'), moment(values[propertyKey]).format('HH:mm:ss'))
          );
        }
        default:
          return false;
      }
    });
  }

  @computed get booleanProperties() {
    return this._enabledProperties.reduce<FilterBooleanProperty[]>((properties, propertyKey: AdvancedFilterProperties) => {
      if (propertyKey === AdvancedFilterProperties.ShowClosed || propertyKey === AdvancedFilterProperties.SeparateAhead) {
        properties.push({
          title: this._availableProperties[propertyKey].title,
          info: {
            on: infoByBooleanState[propertyKey].on,
            off: infoByBooleanState[propertyKey].off,
          },
          key: propertyKey as BooleanProperties,
          checked: this._tempBooleanValues[propertyKey],
        });
      }
      return properties;
    }, []);
  }

  @computed get options() {
    return this._enabledProperties.reduce((options, property: AdvancedFilterProperties) => {
      if (
        property === AdvancedFilterProperties.OrderType ||
        property === AdvancedFilterProperties.Branch ||
        property === AdvancedFilterProperties.OrderingChannel ||
        property === AdvancedFilterProperties.PaymentMethod ||
        property === AdvancedFilterProperties.Status
      ) {
        options[property] = Array.from<AdvancedFilterListOption>(this._availableOptions[property].values());
      }
      return options;
    }, {} as { [key in ListProperties]: AdvancedFilterListOption[] });
  }

  @computed get presetDates() {
    return this._availablePresetDates.map((rangeKey) => presetDatesDataMap[rangeKey]);
  }

  @computed get isChanged() {
    return this._enabledProperties.some(
      (propertyKey) =>
        (availableProperties[propertyKey].type === FilterPropertiesTypes.List &&
          this._selectedOptionsValues[propertyKey as ListProperties].size > 0) ||
        (availableProperties[propertyKey].type === FilterPropertiesTypes.Boolean && this._booleanValues[propertyKey as BooleanProperties]) ||
        (availableProperties[propertyKey].type === FilterPropertiesTypes.Date &&
          this._rangeValues[propertyKey as RangeProperties].get('from') &&
          this._rangeValues[propertyKey as RangeProperties].get('to')) ||
        (availableProperties[propertyKey].type === FilterPropertiesTypes.Time &&
          this._rangeValues[propertyKey as RangeProperties].get('from') &&
          this._rangeValues[propertyKey as RangeProperties].get('to'))
    );
  }

  private _getDateValues = () => {
    const from = this._rangeValues[AdvancedFilterProperties.Date].get('from');
    const to = this._rangeValues[AdvancedFilterProperties.Date].get('to');
    const preset = this._rangeValues[AdvancedFilterProperties.Date].get('preset');

    if (typeof preset === 'number') {
      const range = presetDatesDataMap[preset as DateRanges].getRange();
      return {
        from: moment(new Date(range.from)),
        to: moment(new Date(range.to)),
        preset,
      };
    } else if (from && to) {
      const toDateShouldBeNow = !this.allowDatesInTheFuture && moment(new Date(to)).isAfter(moment());

      return {
        from: moment(new Date(from)),
        to: moment(toDateShouldBeNow ? new Date() : new Date(to)),
        preset: null,
      };
    }
  };

  private _getTimeValues = () => {
    const from = this._rangeValues[AdvancedFilterProperties.Time].get('from');
    const to = this._rangeValues[AdvancedFilterProperties.Time].get('to');

    if (from && to) {
      return {
        from: moment(new Date(from)),
        to: moment(new Date(to)),
        preset: null,
      };
    }
  };

  // Dynamic date values by preset
  get dateValues() {
    const dateValues = this._getDateValues();

    return dateValues
      ? {
          from: dateValues.from.format('YYYY-MM-DD HH:mm:ss'),
          to: dateValues.to.format('YYYY-MM-DD HH:mm:ss'),
          preset: dateValues.preset,
        }
      : null;
  }

  @computed get values() {
    const values = this._enabledProperties.reduce((values, propertyKey) => {
      switch (propertyKey) {
        case AdvancedFilterProperties.ShowClosed:
        case AdvancedFilterProperties.SeparateAhead:
          values[propertyKey] = this._booleanValues[propertyKey];
          break;
        case AdvancedFilterProperties.OrderType:
        case AdvancedFilterProperties.Branch:
        case AdvancedFilterProperties.OrderingChannel:
        case AdvancedFilterProperties.PaymentMethod:
          values[propertyKey] = [...this._selectedOptionsValues[propertyKey].values()].map(({ value }: { value: string }) => value);
          break;
        case AdvancedFilterProperties.Status:
          if (this._selectedOptionsValues[propertyKey].size === 0) {
            values[propertyKey] = [...this._availableOptions[propertyKey].values()].map(({ value }: { value: string }) => value);
          } else {
            values[propertyKey] = [...this._selectedOptionsValues[propertyKey].values()].map(({ value }: { value: string }) => value);
          }
          break;
        case AdvancedFilterProperties.Time: {
          const timeValues = this._getTimeValues();

          if (timeValues) {
            values[AdvancedFilterProperties.Time] = {
              from: moment(timeValues.from).format('HH:mm:ss'),
              to: moment(timeValues.to).format('HH:mm:ss'),
              preset: null,
            };
          }
          break;
        }
        case AdvancedFilterProperties.Date: {
          const dateValues = this._getDateValues();

          if (dateValues) {
            values[AdvancedFilterProperties.Date] = {
              from: dateValues.from.format(dateTimeFormatForRequest),
              to: dateValues.to.format(dateTimeFormatForRequest),
              preset: dateValues.preset,
            };
          }
          break;
        }
        default:
          break;
      }
      return values;
    }, {} as FilterValues);
    return values;
  }
}

export default AdvancedFilterStore;
